import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import * as z from 'zod'
import useMutationLogin from '../../api/hooks/useMutateLogin'
import { useQueryClient } from '@tanstack/react-query'
import { AuthQueryKey } from '../../keys'
import { ROLE_PATH_MAPPERS } from '@/modules/root/mappers'
import { Shield } from 'lucide-react'
import showErrorToast from '@/common/utils/showErrorToast'

const formSchema = z.object({
	username: z.string({ required_error: 'โปรดกรอกชื่อผู้ใช้งาน' }),
	password: z.string({ required_error: 'โปรดกรอกรหัสผ่าน' }),
	totpCode: z.string().optional(),
})

type UserFormValue = z.infer<typeof formSchema>

export default function UserAuthForm() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const queryClient = useQueryClient()

	const { isPending, mutate } = useMutationLogin()

	const form = useForm<UserFormValue>({
		resolver: zodResolver(formSchema),
	})

	const onSubmit = async (data: UserFormValue) => {
		mutate(data, {
			onSuccess: ({ accessToken, role }) => {
				localStorage.setItem('token', accessToken)
				localStorage.setItem('role', role)

				queryClient.resetQueries({
					queryKey: [AuthQueryKey.Profile],
				})

				toast.success('ลงชื่อเข้าใช้สำเร็จ!')

				const redirectPath = searchParams.get('path')
				if (redirectPath) {
					navigate(decodeURIComponent(redirectPath))
				} else {
					navigate(ROLE_PATH_MAPPERS[role])
				}
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	return (
		<>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="w-full space-y-4"
				>
					<FormField
						control={form.control}
						name="username"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ชื่อผู้ใช้งาน</FormLabel>
								<FormControl>
									<Input
										placeholder="กรอกชื่อผู้ใช้งาน..."
										disabled={isPending}
										{...field}
									/>
								</FormControl>
								<FormMessage className="h-4" />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel>รหัสผ่าน</FormLabel>
								<FormControl>
									<Input
										type="password"
										placeholder="กรอกรหัสผ่าน..."
										disabled={isPending}
										{...field}
									/>
								</FormControl>
								<FormMessage className="h-4" />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="totpCode"
						render={({ field }) => (
							<FormItem className="rounded-lg border p-4">
								<FormLabel className="flex items-center gap-2">
									<Shield className="h-4 w-4 text-blue-500" />
									<span>รหัสผ่าน 2FA</span>
								</FormLabel>
								<FormDescription className="text-sm text-blue-600">
									กรุณากรอกรหัส 2FA จากแอปพลิเคชันที่ท่านใช้งาน เช่น Google
									Authenticator
								</FormDescription>
								<FormControl>
									<Input
										className="font-mono text-lg tracking-wider"
										placeholder="000000"
										maxLength={6}
										disabled={isPending}
										{...field}
									/>
								</FormControl>
								<FormMessage className="h-4" />
							</FormItem>
						)}
					/>

					<Button disabled={isPending} className="ml-auto w-full" type="submit">
						เข้าสู่ระบบ
					</Button>
				</form>
			</Form>
		</>
	)
}
